import { useCallback, useRef } from 'react';
import { TextField, InputAdornment } from '@mui/material';
import SpeechToTextMicButton, { scrollFieldToEnd } from './SpeechToTextMicButton';

const NON_SPEECH_TYPES = new Set([
  'number',
  'date',
  'time',
  'datetime-local',
  'month',
  'week',
  'color',
  'range',
  'file',
]);

/**
 * TextField with a shared speech-to-text mic button in the end adornment.
 * Drop-in replacement for MUI TextField on free-text inputs.
 * Number/date fields never get a mic (avoids browser parse warnings).
 */
const SpeechTextField = ({
  value,
  onChange,
  name,
  type,
  InputProps,
  inputRef: externalInputRef,
  disabled = false,
  speechDisabled = false,
  speechLang = 'en-IN',
  multiline,
  ...rest
}) => {
  const fieldValue = value == null ? '' : String(value);
  const inputRef = useRef(null);

  // Number/date inputs cannot accept spoken sentences — hide mic for those
  const isNonSpeechType = NON_SPEECH_TYPES.has(type);
  const showMic = !disabled && !speechDisabled && !isNonSpeechType;

  const setInputRef = useCallback(
    (node) => {
      inputRef.current = node;
      if (!externalInputRef) return;
      if (typeof externalInputRef === 'function') {
        externalInputRef(node);
      } else {
        externalInputRef.current = node;
      }
    },
    [externalInputRef],
  );

  const handleSpeechResult = useCallback(
    (text) => {
      if (typeof onChange !== 'function') return;
      const nextValue = text == null ? '' : String(text);
      onChange({
        target: {
          name,
          value: nextValue,
          type: type || 'text',
        },
        currentTarget: {
          name,
          value: nextValue,
          type: type || 'text',
        },
      });
      scrollFieldToEnd(inputRef.current);
    },
    [name, onChange, type],
  );

  return (
    <TextField
      {...rest}
      name={name}
      type={type}
      value={fieldValue}
      onChange={onChange}
      disabled={disabled}
      multiline={multiline}
      inputRef={setInputRef}
      InputProps={{
        ...InputProps,
        endAdornment: (
          <>
            {InputProps?.endAdornment}
            {showMic && (
              <InputAdornment
                position="end"
                sx={multiline ? { alignSelf: 'flex-start', mt: 0.5 } : undefined}
              >
                <SpeechToTextMicButton
                  value={fieldValue}
                  onResult={handleSpeechResult}
                  disabled={disabled}
                  lang={speechLang}
                  inputRef={inputRef}
                />
              </InputAdornment>
            )}
          </>
        ),
        ...(multiline
          ? {
              sx: {
                alignItems: 'flex-start',
                ...(InputProps?.sx || {}),
              },
            }
          : InputProps?.sx
            ? { sx: InputProps.sx }
            : {}),
      }}
    />
  );
};

export default SpeechTextField;
